import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Globe, BarChart3, Users, FolderKanban, Activity, Plug,
  LogOut, Loader2, ArrowUpRight, ShieldCheck, ExternalLink, RefreshCw, Check, Wand2, Download, Bot,
  MessagesSquare, HelpCircle, ReceiptText, ShieldCheck as ShieldIcon,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, CartesianGrid } from "recharts";
import { BrandLogo } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import {
  getOverview, getWebsites, getProjects, getPortalLeads, getAnalytics,
  getIntegrations, connectIntegration, runHealthCheck, scoreTone, portalDashboardLink,
  getRedesigns, rebuildPdfBlob,
} from "@/lib/api";
import { toast } from "sonner";
import Chatbots from "@/components/portal/Chatbots";
import { Conversations, Unanswered, Invoices, Privacy } from "@/components/portal/CustomerDashboard";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "websites", label: "My Websites", icon: Globe },
  { key: "redesigns", label: "AI Redesigns", icon: Wand2 },
  { key: "chatbots", label: "AI Chatbots", icon: Bot },
  { key: "conversations", label: "AI Conversations", icon: MessagesSquare },
  { key: "unanswered", label: "Unanswered", icon: HelpCircle },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "leads", label: "Leads", icon: Users },
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "health", label: "Website Health", icon: Activity },
  { key: "invoices", label: "Invoices", icon: ReceiptText },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "privacy", label: "Data & Privacy", icon: ShieldIcon },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === null) navigate("/login");
  }, [user, navigate]);

  if (user === undefined || user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 fixed h-full">
        <div className="p-6 border-b border-slate-100"><Link to="/"><BrandLogo size={36} /></Link></div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              data-testid={`portal-nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === n.key ? "bg-blue-50 text-brand" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <n.icon size={17} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="px-2 mb-3">
            <p className="text-sm font-semibold text-slate-800">{user.name}</p>
            <p className="text-xs text-slate-400">{user.company || user.email}</p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-slate-500 hover:bg-slate-50" data-testid="portal-logout">
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0 max-w-full">
        {/* Mobile tabs */}
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <BrandLogo size={30} />
          <button onClick={() => { logout(); navigate("/"); }} className="text-slate-500"><LogOut size={18} /></button>
        </div>
        <div className="lg:hidden bg-white border-b border-slate-200 overflow-x-auto flex gap-1 px-3 py-2 sticky top-[57px] z-10">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`portal-mtab-${n.key}`} className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium ${tab === n.key ? "bg-blue-50 text-brand" : "text-slate-500"}`}>{n.label}</button>
          ))}
        </div>

        <main className="p-6 md:p-10 max-w-6xl">
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {tab === "overview" && <Overview user={user} />}
            {tab === "websites" && <Websites />}
            {tab === "redesigns" && <Redesigns />}
            {tab === "chatbots" && <Chatbots />}
            {tab === "conversations" && <Conversations />}
            {tab === "unanswered" && <Unanswered />}
            {tab === "analytics" && <Analytics />}
            {tab === "leads" && <Leads />}
            {tab === "projects" && <Projects />}
            {tab === "health" && <Health />}
            {tab === "invoices" && <Invoices />}
            {tab === "integrations" && <Integrations />}
            {tab === "privacy" && <Privacy />}
          </motion.div>
        </main>
      </div>
    </div>
  );
}

function Header({ title, sub, right }) {
  return (
    <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
        {sub && <p className="text-slate-500 mt-1">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
      <p className="overline">{label}</p>
      <p className="font-heading text-3xl font-extrabold mt-2" style={{ color: tone || "#0F172A" }}>{value}</p>
    </div>
  );
}

function useData(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    setLoading(true);
    fn().then((d) => ok && setData(d)).catch(() => {}).finally(() => ok && setLoading(false));
    return () => { ok = false; };
    // eslint-disable-next-line
  }, deps);
  return { data, loading };
}

const Spin = () => <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand" size={24} /></div>;

function Redesigns() {
  const { data, loading } = useData(getRedesigns);
  const open = async (id) => {
    try {
      const url = await rebuildPdfBlob(id);
      const win = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!win) toast.error("Please allow pop-ups to open the report.");
    } catch { toast.error("Could not open the PDF."); }
  };
  if (loading || !data) return <Spin />;
  const items = data.redesigns || [];
  return (
    <>
      <Header title="AI Redesigns" sub="Every website rebuild concept you've unlocked — download the PDF anytime." />
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 text-center" data-testid="portal-redesigns-empty">
          <Wand2 className="mx-auto text-brand mb-3" size={28} />
          <p className="text-slate-600 font-medium">No redesigns saved yet.</p>
          <p className="text-slate-400 text-sm mt-1">Run the Website Analyzer and unlock a rebuild concept to build your gallery.</p>
          <a href="/#analyzer" className="text-brand font-semibold text-sm mt-4 inline-block">Analyze a website →</a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portal-redesigns-grid">
          {items.map((item) => (
            <div key={item.id} className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden flex flex-col" data-testid="portal-redesign-card">
              <div className="h-20 flex">
                {(item.palette || []).length ? item.palette.map((p, i) => <div key={i} className="flex-1" style={{ background: p.hex }} />) : <div className="flex-1 bg-slate-800" />}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <p className="text-[10px] font-mono text-slate-400">{item.domain}</p>
                <h3 className="font-heading text-base font-bold text-slate-900 mt-1 leading-snug">{item.headline || "Rebuild concept"}</h3>
                {item.subheadline && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{item.subheadline}</p>}
                <button onClick={() => open(item.id)} data-testid="portal-redesign-download" className="mt-auto pt-4 flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-all">
                  <Download size={15} /> Download PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function Overview({ user }) {
  const { data, loading } = useData(getOverview);
  const [shareToken, setShareToken] = useState(null);
  useEffect(() => { portalDashboardLink().then((d) => setShareToken(d.share_token)).catch(() => {}); }, []);
  if (loading || !data) return <Spin />;
  return (
    <>
      <Header title={`Welcome back, ${user.name.split(" ")[0]}`} sub="Your website performance at a glance." />
      {shareToken && (
        <a href={`/dashboard/${shareToken}`} target="_blank" rel="noopener noreferrer" data-testid="portal-dashboard-link"
          className="flex items-center justify-between gap-3 bg-brand/5 border border-brand/15 rounded-2xl p-4 mb-6 hover:border-brand/30 transition-colors">
          <span className="text-sm text-slate-600 flex items-center gap-2"><ExternalLink size={15} className="text-brand" /> Open your shareable performance dashboard</span>
          <span className="text-xs font-semibold text-brand">View →</span>
        </a>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Websites" value={data.websites} />
        <Stat label="Avg Score" value={data.avg_score} tone={scoreTone(data.avg_score).color} />
        <Stat label="Active Projects" value={data.active_projects} />
        <Stat label="Open Leads" value={data.open_leads} tone="#2563EB" />
      </div>
      <Link to="/portal/assistants" data-testid="portal-assistants-cta"
        className="mt-6 flex items-center justify-between gap-3 rounded-2xl p-5 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white hover:from-indigo-500 hover:to-indigo-700 transition group">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-white/20 grid place-items-center">
            <Wand2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-indigo-200 font-bold">New</div>
            <div className="text-lg font-bold">AI Assistants Hub — Website Auditor, Proposal Writer, Sales Coach</div>
            <div className="text-sm text-indigo-100 mt-0.5">Three purpose-built AI assistants ready to use right now.</div>
          </div>
        </div>
        <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
          <p className="overline mb-4">Your Websites</p>
          <div className="space-y-3">
            {data.site_list.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-slate-100 rounded-xl p-3">
                <div><p className="font-semibold text-slate-800">{s.name}</p><p className="text-xs text-slate-400">{s.domain}</p></div>
                <span className="font-heading font-bold text-xl" style={{ color: scoreTone(s.score).color }}>{s.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
          <p className="overline mb-4">Recent Leads</p>
          <div className="space-y-2">
            {data.recent_leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div><p className="text-sm font-medium text-slate-800">{l.name}</p><p className="text-xs text-slate-400">{l.service} · {l.source}</p></div>
                <LeadBadge score={l.score} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function LeadBadge({ score }) {
  const map = { hot: ["#DC2626", "#FEF2F2", "Hot"], warm: ["#F59E0B", "#FFFBEB", "Warm"], cold: ["#64748B", "#F8FAFC", "Cold"] };
  const [c, bg, t] = map[score] || map.cold;
  return <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ color: c, background: bg }}>{t}</span>;
}

function Websites() {
  const { data, loading } = useData(getWebsites);
  if (loading || !data) return <Spin />;
  return (
    <>
      <Header title="My Websites" sub="Every site WebsiteTech manages for you." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {data.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6" data-testid="portal-website">
            <div className="flex items-start justify-between">
              <div><h3 className="font-heading text-xl font-bold text-slate-900">{s.name}</h3><p className="text-sm text-slate-400">{s.domain}</p></div>
              <span className="font-heading text-3xl font-extrabold" style={{ color: scoreTone(s.score).color }}>{s.score}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
              <Field label="Industry" value={s.industry} />
              <Field label="Status" value={s.status} />
              <Field label="CMS" value={s.cms} />
              <Field label="Launch" value={s.launch_date} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Field({ label, value }) {
  return <div><p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p><p className="text-slate-700 font-medium capitalize">{value}</p></div>;
}

function Analytics() {
  const [rng, setRng] = useState(30);
  const { data, loading } = useData(() => getAnalytics(rng), [rng]);
  return (
    <>
      <Header
        title="Analytics"
        sub="Visitor, lead and conversion intelligence."
        right={
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setRng(d)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${rng === d ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{d}d</button>
            ))}
          </div>
        }
      />
      <div className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> DEMO DATA — connect Google Analytics for live numbers
      </div>
      {loading || !data ? <Spin /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Visitors" value={data.visitors.toLocaleString()} />
            <Stat label="Sessions" value={data.sessions.toLocaleString()} />
            <Stat label="Leads" value={data.leads} tone="#2563EB" />
            <Stat label="Conversion" value={`${data.conversion_rate}%`} tone="#16A34A" />
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 mt-6">
            <p className="overline mb-4">Visitors & Leads</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.series} margin={{ left: -20, right: 10 }}>
                <defs>
                  <linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                  <linearGradient id="l" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16A34A" stopOpacity={0.3} /><stop offset="100%" stopColor="#16A34A" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Area type="monotone" dataKey="visitors" stroke="#2563EB" strokeWidth={2} fill="url(#v)" />
                <Area type="monotone" dataKey="leads" stroke="#16A34A" strokeWidth={2} fill="url(#l)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
              <p className="overline mb-4">Traffic Sources</p>
              {data.sources.map((s) => (
                <div key={s.source} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                  <span className="text-sm font-medium text-slate-700">{s.source}</span>
                  <div className="flex items-center gap-4"><span className="text-sm text-slate-500">{s.visitors.toLocaleString()}</span><span className="text-xs font-mono text-green-600 w-12 text-right">{s.conv}%</span></div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
              <p className="overline mb-4">Top Pages</p>
              {data.top_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                  <span className="text-sm font-mono text-slate-700">{p.path}</span><span className="text-sm text-slate-500">{p.views.toLocaleString()} views</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Leads() {
  const { data, loading } = useData(getPortalLeads);
  if (loading || !data) return <Spin />;
  return (
    <>
      <Header title="Leads" sub="Prospects captured from your websites." />
      <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden">
        <div className="hidden md:grid grid-cols-5 gap-4 px-6 py-3 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-400">
          <span>Name</span><span>Service</span><span>Source</span><span>Status</span><span className="text-right">Score</span>
        </div>
        {data.map((l) => (
          <div key={l.id} className="grid grid-cols-2 md:grid-cols-5 gap-4 px-6 py-4 border-b border-slate-50 last:border-0 items-center" data-testid="portal-lead">
            <div><p className="font-medium text-slate-800">{l.name}</p><p className="text-xs text-slate-400 md:hidden">{l.email}</p></div>
            <span className="text-sm text-slate-600">{l.service}</span>
            <span className="text-sm text-slate-500 hidden md:block">{l.source}</span>
            <span className="text-xs font-semibold capitalize text-slate-600 hidden md:block">{l.status}</span>
            <div className="text-right"><LeadBadge score={l.score} /></div>
          </div>
        ))}
      </div>
    </>
  );
}

function Projects() {
  const { data, loading } = useData(getProjects);
  if (loading || !data) return <Spin />;
  return (
    <>
      <Header title="Projects" sub="What WebsiteTech is building for you." />
      <div className="space-y-4">
        {data.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6" data-testid="portal-project">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div><h3 className="font-heading text-lg font-bold text-slate-900">{p.name}</h3><p className="text-sm text-slate-400">{p.website} · target {p.target_date}</p></div>
              <span className="text-xs font-semibold capitalize px-3 py-1 rounded-full bg-blue-50 text-brand">{p.status}</span>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${p.progress}%` }} /></div>
              <span className="font-mono text-sm text-slate-500">{p.progress}%</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

const SEV = { critical: ["#DC2626", "#FEF2F2"], warning: ["#F59E0B", "#FFFBEB"], notice: ["#2563EB", "#EFF6FF"], resolved: ["#16A34A", "#F0FDF4"] };

function Health() {
  const [url, setUrl] = useState("marinotrattoria.com");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true); setResult(null);
    try { setResult(await runHealthCheck(url.trim())); }
    catch { toast.error("Could not reach that website."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Header title="WebsiteTech Watch™" sub="Real-time uptime, SSL, speed and broken-link checks." />
      <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Enter a website URL" className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 outline-none focus:border-brand" data-testid="health-url" />
          <button onClick={run} disabled={loading} data-testid="health-run" className="btn-primary rounded-lg px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <><RefreshCw size={16} /> Run Check</>}
          </button>
        </div>
        {loading && <p className="text-sm text-slate-400 mt-4 font-mono">Running live checks against {url}…</p>}
        {result && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={18} className={result.overall === "healthy" ? "text-green-600" : result.overall === "warning" ? "text-amber-500" : "text-red-500"} />
              <span className="font-semibold text-slate-800 capitalize">Overall: {result.overall}</span>
              <span className="text-xs text-slate-400 font-mono ml-auto">{result.domain}</span>
            </div>
            <div className="space-y-2">
              {result.checks.map((c, i) => {
                const [col, bg] = SEV[c.severity] || SEV.notice;
                return (
                  <div key={i} className="flex items-start gap-3 border border-slate-100 rounded-xl p-3" data-testid="health-check-item">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded mt-0.5" style={{ color: col, background: bg }}>{c.severity}</span>
                    <div><p className="text-sm font-semibold text-slate-800">{c.check}</p><p className="text-xs text-slate-500">{c.detail}</p></div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Integrations() {
  const [items, setItems] = useState(null);
  useEffect(() => { getIntegrations().then(setItems); }, []);
  const toggle = async (key, connected) => {
    setItems((it) => it.map((i) => (i.key === key ? { ...i, connected } : i)));
    try { await connectIntegration(key, connected); toast.success(connected ? "Connected (demo)" : "Disconnected"); }
    catch { toast.error("Failed"); }
  };
  if (!items) return <Spin />;
  return (
    <>
      <Header title="Integration Center" sub="Connect analytics, SEO, payments, CRM and more." />
      <div className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> DEMO — real OAuth (incl. Google & SocialPulse SSO) wires in when credentials are provided
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((i) => (
          <div key={i.key} className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 flex items-center justify-between" data-testid={`integration-${i.key}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500"><Plug size={18} /></div>
              <div><p className="font-semibold text-slate-800">{i.name}</p><p className="text-xs text-slate-400">{i.category}</p></div>
            </div>
            {i.connected ? (
              <button onClick={() => toggle(i.key, false)} className="text-sm font-medium text-green-600 flex items-center gap-1.5"><Check size={15} /> Connected</button>
            ) : (
              <button onClick={() => toggle(i.key, true)} className="btn-outline rounded-lg px-4 py-2 text-sm flex items-center gap-1.5">Connect <ExternalLink size={13} /></button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
